import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, signal } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule } from '@ngx-translate/core';
import { PERSON_API_SERVICE_TOKEN, SCREENING_CONFIG_API_SERVICE_TOKEN } from '@nk/api-lib';
import { AddToScreeningFormValue } from '@nk/data-entities-lib';
import { ButtonDirective, ErrorAlertComponent, ProgressComponent, SpinnerComponent } from '@nk/ui-lib';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { MonitoringConnectionService } from '../../services/monitoring-connection.service';
import { MonitoringOriginEntityService } from '../../services/monitoring-origin-entity.service';
import { ScreeningCaseService } from '../../services/screening-case.service';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';
import {
  AddToScreeningCustomerPersonProps,
  AddToScreeningModalProps,
  AddToScreeningPersonProps,
  DEFAULT_MONITORING_ADD_DECISION_COMMENT,
} from '../../utils/screening.utils';
import { AddToScreeningFormComponent } from '../add-to-screening-form/add-to-screening-form.component';

@Component({
  selector: 'nk-add-to-screening-modal',
  imports: [
    TranslateModule,
    ButtonDirective,
    ProgressComponent,
    SpinnerComponent,
    AddToScreeningFormComponent,
    ErrorAlertComponent,
  ],
  templateUrl: './add-to-screening-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToScreeningModalComponent implements AddToScreeningPersonProps, AddToScreeningCustomerPersonProps {
  @Input() public title: string = extractTranslation('screening.set_on_monitoring_modal.title');
  @Input() public screeningConfigUuid!: AddToScreeningModalProps['screeningConfigUuid'];
  @Input() public monitoringOriginEntityUuid!: AddToScreeningModalProps['monitoringOriginEntityUuid'];
  @Input()
  public entityMonitoringTypeUuid!: AddToScreeningModalProps['entityMonitoringTypeUuid'];
  @Input() public appMembershipUuid!: AddToScreeningModalProps['appMembershipUuid'];
  @Input() public personUuid!: AddToScreeningPersonProps['personUuid'];
  @Input()
  public customerPersonUuid!: AddToScreeningCustomerPersonProps['customerPersonUuid'];
  public error = signal<HttpErrorResponse | null>(null);
  public addToScreeningFormValue: AddToScreeningFormValue | null = null;
  public isFormValid = signal(false);
  public isLoading = signal(false);
  private _bsModalRef = inject(BsModalRef);
  private _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private _monitoringConnectionService = inject(MonitoringConnectionService);
  private _monitoringOriginEntityService = inject(MonitoringOriginEntityService);
  private _screeningCaseService = inject(ScreeningCaseService);
  private _personService = inject(PERSON_API_SERVICE_TOKEN);
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _cdr = inject(ChangeDetectorRef);

  public onValueChanged(data: AddToScreeningFormValue): void {
    this.addToScreeningFormValue = data;
  }
  public onFormValidityChanged(valid: boolean): void {
    this.isFormValid.set(valid);
    this._cdr.detectChanges();
  }

  public cancelAndClose(): void {
    this._bsModalRef.hide();
  }

  public addAndClose(): void {
    const entityMonitoringTypeUuid = this.addToScreeningFormValue?.entity_monitoring_type_uuid;
    if (this.addToScreeningFormValue && entityMonitoringTypeUuid) {
      this.isLoading.set(true);
      this.error.set(null);
      this._screeningConfigService
        .postScreeningEntityEntity({
          entity_uuid: this.personUuid ? this.personUuid : null,
          entity_key: this.personUuid ? 'person' : 'custom_person',
          customer_person_uuid: this.customerPersonUuid ? this.customerPersonUuid : null,
        })
        .pipe(
          switchMap((screeningEntity) => {
            return this._screeningConfigService.postScreeningActionEntity(this.screeningConfigUuid, {
              monitoring_origin_entity_uuid: this.monitoringOriginEntityUuid,
              app_membership_uuid: this.appMembershipUuid,
              screening_entity_uuid: screeningEntity.uuid,
              removed_flag: false,
              approved_flag: true,
              monitoring_comment: this.addToScreeningFormValue?.monitoring_comment ?? '',
              decision_comment: DEFAULT_MONITORING_ADD_DECISION_COMMENT,
              entity_monitoring_type_uuid: entityMonitoringTypeUuid,
            });
          }),
          catchError((error) => {
            this.error.set(error);
            return of(null);
          }),
          finalize(() => {
            this.isLoading.set(false);
            this._cdr.markForCheck();
          }),
        )
        .subscribe(() => {
          this._monitoringOriginEntityService.monitoringOriginEntityCompositeCollectionCache.invalidateAllCache();
          this._screeningCaseService.screeningEntityScreeningCaseCompositeCollectionCache.invalidateAllCache();
          this._monitoringConnectionService.monitoringConnectionCompositeCollectionCache.invalidateAllCache();
          this._personService.personRoleCompositeCollectionByPersonCache.invalidateAllCache();
          this._screeningConfigService.screeningActionCompositeCollectionCache.invalidateAllCache();
          this._screeningConfigService.screeningConfigStatsAggregateCache.invalidateAllCache();
          this._screeningInputParamsService.triggerScreeningEntitiesRefresh();
          this._bsModalRef.hide();
        });
    }
  }
}
